import styled, { keyframes, useTheme, css } from 'styled-components';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import {
  Heading3Text,
  ParagraphLargeText,
  BodyLarge,
} from '../../../components/Texts';
import WidthContainer from '../../../components/WidthContainer';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import Button from '../../../components/Button';
import GiftCard from '../../../components/GiftCard';
import Particles from './Particles';
import { useImagePreload } from '../../../common/images';
import StoreButton from '../../../components/StoreButton';
import { useFlag } from '@unleash/proxy-client-react';
import { Capacitor } from '@capacitor/core';

// Exciting entrance animations
const giftCardEntrance = keyframes`
  0% {
    transform: scale(0.3) rotateY(-15deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.05) rotateY(5deg);
    opacity: 0.8;
  }
  100% {
    transform: scale(1) rotateY(0deg);
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const ClaimGiftContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  align-items: center;
  margin-top: 50px;
  gap: 20px;
  z-index: 10;
  max-width: 516px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 15px;
    gap: 0px;
  }
`;

const GiftContainer = styled.div`
  text-align: start;
  align-items: start;
  width: 100%;
  position: relative;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  z-index: 10;

  @media ${PHONE_MEDIA_QUERY} {
    margin-bottom: 20px;
  }
`;

const explodeAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
    opacity: 0;
  }
`;

const GiftCardWrapper = styled.div`
  width: 100%;
  max-width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '516px')};
  border-radius: 15px;
  overflow: visible;
  opacity: 1;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '407px')};
  }
`;

const CardAnimationWrapper = styled.div`
  animation: ${giftCardEntrance} 1.2s ease-out forwards;
  animation-delay: 0.5s;
  animation-fill-mode: both;
  box-shadow: 0 0 80px rgba(38, 60, 208, 0.25);
  border-radius: 15px;
`;

const appearAnimation = keyframes`
  0% {
    opacity: 1;
    transform: scale(0.5);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const wrapperAnimationMap = {
  reveal: appearAnimation,
  explode: explodeAnimation,
};

const CardExplosionAnimationWrapper = styled.div`
  animation: ${({ animationType }) => wrapperAnimationMap[animationType]} 0.25s
    ease-in-out forwards;
  box-shadow: 0 0 80px rgba(38, 60, 208, 0.25);
  border-radius: 15px;
  position: relative;
  z-index: 10;
`;

const CardDetailRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  min-height: 24px;
  ${({ $visible }) =>
    $visible &&
    css`
      animation: ${fadeInUp} 0.8s ease-out;
      animation-fill-mode: both;
      animation-delay: 0.8s;
    `}
`;

const MessageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  min-height: 40px;
  ${({ $visible }) =>
    $visible &&
    css`
      animation: ${fadeInUp} 0.8s ease-out;
      animation-fill-mode: both;
      animation-delay: 0.9s;
    `}
`;

const StyledButton = styled(Button)`
  width: 100%;
  max-width: 200px;
  animation: ${fadeInUp} 0.8s ease-out;
  animation-fill-mode: both;
  animation-delay: ${({ animated }) => (animated ? '1.7s' : '.5s')};
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 8px;
    max-width: unset;
  }
`;

const StyledHeading3Text = styled(Heading3Text)`
  animation: ${fadeInUp} 0.8s ease-out;
  animation-fill-mode: both;
  animation-delay: 0.2s;

  @media ${PHONE_MEDIA_QUERY} {
    font-size: 24px;
  }
`;

const StyledParagraphLargeText = styled(ParagraphLargeText)`
  animation: ${fadeInUp} 0.8s ease-out;
  animation-fill-mode: both;
  animation-delay: 0.4s;

  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  margin-top: 20px;
  animation: ${fadeInUp} 0.8s ease-out;
  animation-fill-mode: both;
  animation-delay: ${({ animated }) => (animated ? '1.7s' : '.5s')};

  @media ${PHONE_MEDIA_QUERY} {
    gap: 0px;
  }
`;

const shakeAnimation = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg) scale(1);
  }
  5% {
    transform: translate(-1px, -0.5px) rotate(-0.5deg) scale(1.01);
  }
  10% {
    transform: translate(1.5px, 0.5px) rotate(0.4deg) scale(1.02);
  }
  15% {
    transform: translate(-0.5px, 1.2px) rotate(-0.7deg) scale(1.03);
  }
  20% {
    transform: translate(0.8px, -0.7px) rotate(0.6deg) scale(1.03);
  }
  25% {
    transform: translate(-1.2px, 0.6px) rotate(-0.9deg) scale(1.04);
  }
  30% {
    transform: translate(1px, -1.3px) rotate(0.8deg) scale(1.05);
  }
  35% {
    transform: translate(-0.7px, 0.9px) rotate(-1.1deg) scale(1.06);
  }
  40% {
    transform: translate(0.9px, 1.3px) rotate(0.9deg) scale(1.07);
  }
  45% {
    transform: translate(-1.4px, -0.8px) rotate(-1.2deg) scale(1.08);
  }
  50% {
    transform: translate(1.3px, 0.7px) rotate(1.1deg) scale(1.09);
  }
  55% {
    transform: translate(-1.8px, 1.1px) rotate(-1.3deg) scale(1.1);
  }
  60% {
    transform: translate(1.6px, -1.2px) rotate(1.2deg) scale(1.11);
  }
  65% {
    transform: translate(-1.2px, 1.6px) rotate(-1.5deg) scale(1.12);
  }
  70% {
    transform: translate(1.5px, -1.6px) rotate(1.4deg) scale(1.13);
  }
  75% {
    transform: translate(-2px, 1.2px) rotate(-1.7deg) scale(1.14);
  }
  80% {
    transform: translate(2.1px, -1.4px) rotate(1.6deg) scale(1.15);
  }
  85% {
    transform: translate(-1.7px, 2px) rotate(-1.9deg) scale(1.16);
  }
  90% {
    transform: translate(2.2px, -2px) rotate(2deg) scale(1.18);
  }
  95% {
    transform: translate(-2.3px, 1.8px) rotate(-2.1deg) scale(1.19);
  }
  100% {
    transform: translate(2.5px, -1.7px) rotate(2.2deg) scale(1.2);
  }
`;

const occasionalMovement = keyframes`
  0%, 95%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  53% {
    transform: translate(1px, -0.5px) rotate(0.6deg);
  }
  54% {
    transform: translate(-0.5px, 0.5px) rotate(-0.5deg);
  }
  55% {
    transform: translate(0.5px, 0) rotate(0.3deg);
  }
  56% {
    transform: translate(0, 0) rotate(0deg);
  }
  96% {
    transform: translate(1px, -0.5px) rotate(0.6deg);
  }
  97% {
    transform: translate(-0.5px, 0.5px) rotate(-0.5deg);
  }
  98% {
    transform: translate(0.5px, 0) rotate(0.3deg);
  }
  99% {
    transform: translate(0, 0) rotate(0deg);
  }
`;

const animations = {
  shake: shakeAnimation,
  explode: explodeAnimation,
  occasional: occasionalMovement,
};

const HiddenGiftCard = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 15px;
  overflow: hidden;
  animation: ${({ animationType }) => animations[animationType]}
    ${({ animationDuration }) => animationDuration} ease-in-out
    ${({ animationType }) =>
      animationType === 'occasional' ? 'infinite' : 'infinite'};
`;

const ClickableWrapper = styled.div`
  position: relative;
  width: 100%;
  cursor: pointer;
  user-select: none;
`;

const fadeInLabel = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const ClickToRevealLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  pointer-events: none;
  animation: ${fadeInLabel} 0.6s ease-out forwards;
  animation-delay: 0.3s;
  opacity: 0;
  backdrop-filter: blur(4px);
  text-align: center;
  white-space: nowrap;

  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
    padding: 10px 20px;
  }
`;

export const WRAPPING_TYPE_MAP = {
  default: '/hidden-gift.webp',
  christmas: '/christmas-gift-card.webp',
  halloween: '/halloween-gift-card.webp',
  breakpoint: '/breakpoint-gift-card.webp',
  valentines: '/valentines-gift-card.webp',
};

const PresentationalGift = ({
  usdAmount,
  tokenSymbol,
  tokenAmount,
  token,
  giftCardImageUrl,
  onClick,
  email,
  message,
  buttonText,
  status,
  htlcExpiry,
  showStatus = false,
  animated = false,
  giftId,
  wrappingType = 'default',
  isCreator = false,
  isReceiver = false,
  hideText = false,
  hideButton = false,
  revealText,
  noWidthContainer = false,
  lpd,
  showEarnsText = false,
}) => {
  const { t } = useTranslation();
  const isClaimed = ['HTLC_CLAIMED', 'CLAIMED'].includes(status);
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const [animationStarted, setAnimationStarted] = useState(false);
  const theme = useTheme();
  const useHalloweenWrapping = useFlag('useHalloweenWrapping');
  const useValentinesWrapping = useFlag('useValentinesWrapping');
  const showAppDownload = useFlag('showAppDownload');

  const shouldAnimate = animated && !isClaimed && animationStarted;

  const [revelead, setRevealed] = useState(false);
  const [explotion, setExplotion] = useState(false);
  const [cardReference, setCardReference] = useState(null);
  const [wrapperReference, setWrapperReference] = useState(null);
  const [animationPosition, setAnimationPosition] = useState({
    x: 0,
    y: 0,
  });
  const [userClickedReveal, setUserClickedReveal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (shouldAnimate) {
      setAnimationStarted(true);
      setTimeout(() => {
        setExplotion(true);
        setTimeout(() => {
          localStorage.setItem(`gift-${giftId}`, 'true');
          setRevealed(true);
        }, 200);
      }, 1500);
    }
  }, [shouldAnimate, giftCardImageUrl]);

  const handleRevealClick = () => {
    if (revelead || isAnimating) return;

    // Calculate position synchronously right before explosion
    // Use wrapperReference (GiftCardWrapper) which is always present and stable
    // This avoids issues with transforms on parent containers (StaggerAnimShell, StepWrapper)
    const referenceElement = wrapperReference || cardReference;
    if (referenceElement) {
      const giftCardRect = referenceElement.getBoundingClientRect();
      console.log(
        '[PresentationalGift] Calculating position in handleRevealClick:',
        {
          giftCardRect,
          windowScrollX: window.scrollX,
          windowScrollY: window.scrollY,
          usingWrapper: !!wrapperReference,
          usingCard: !!cardReference && !wrapperReference,
        },
      );
      if (giftCardRect && giftCardRect.width > 0 && giftCardRect.height > 0) {
        // Use getBoundingClientRect which already accounts for scroll and transforms
        // Calculate center of the card
        const explosionX = giftCardRect.left + giftCardRect.width / 2;
        const explosionY = giftCardRect.top + giftCardRect.height / 2;
        setAnimationPosition({
          x: explosionX,
          y: explosionY,
        });
      } else {
        console.warn('[PresentationalGift] giftCardRect invalid:', {
          width: giftCardRect?.width,
          height: giftCardRect?.height,
        });
      }
    } else {
      console.warn(
        '[PresentationalGift] No reference element available in handleRevealClick',
      );
    }

    setUserClickedReveal(true);
    setIsAnimating(true);

    // Play sound
    if (audioRef.current) {
      audioRef.current.play().catch((error) => {
        console.warn('Failed to play sound:', error);
      });
    }

    // Trigger the same animation sequence as shouldAnimate
    setAnimationStarted(true);
    setTimeout(() => {
      setExplotion(true);
      setTimeout(() => {
        localStorage.setItem(`gift-${giftId}`, 'true');
        setRevealed(true);
        setIsAnimating(false);
      }, 200);
    }, 1500);
  };

  useEffect(() => {
    useImagePreload(giftCardImageUrl);
  }, [giftCardImageUrl]);

  useEffect(() => {
    // Initialize audio element
    audioRef.current = new Audio('/sounds/revealGift.mp3');
    audioRef.current.volume = 0.7;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Show label after image is loaded with a delay
    if (imageLoaded && !userClickedReveal && !shouldAnimate && !revelead) {
      const timer = setTimeout(() => {
        setShowLabel(true);
      }, 500); // 500ms delay after image loads

      return () => clearTimeout(timer);
    } else {
      setShowLabel(false);
    }
  }, [imageLoaded, userClickedReveal, shouldAnimate, revelead]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  console.log('Usd amount', usdAmount);

  // Note: Position calculation is now done in handleRevealClick right before explosion
  // This useEffect is kept only for auto-animation case (shouldAnimate)
  useEffect(() => {
    const referenceElement = wrapperReference || cardReference;
    if (referenceElement && shouldAnimate && !userClickedReveal) {
      // Only calculate for auto-animation, not manual clicks
      if (animationPosition.x === 0 || animationPosition.y === 0) {
        const giftCardRect = referenceElement?.getBoundingClientRect();
        console.log(
          '[PresentationalGift] Calculating position in useEffect (auto-animate):',
          {
            giftCardRect,
            windowScrollX: window.scrollX,
            windowScrollY: window.scrollY,
            usingWrapper: !!wrapperReference,
          },
        );
        if (giftCardRect && giftCardRect.width > 0 && giftCardRect.height > 0) {
          const explosionX = giftCardRect.left + giftCardRect.width / 2;
          const explosionY = giftCardRect.top + giftCardRect.height / 2;
          console.log(
            '[PresentationalGift] Setting animation position from useEffect:',
            {
              explosionX,
              explosionY,
              calculatedFrom: 'useEffect (auto-animate)',
            },
          );
          setAnimationPosition({
            x: explosionX,
            y: explosionY,
          });
        } else {
          console.warn(
            '[PresentationalGift] giftCardRect invalid in useEffect',
          );
        }
      }
    }
  }, [
    wrapperReference,
    cardReference,
    shouldAnimate,
    userClickedReveal,
    animationPosition,
  ]);

  const CardAWrapper =
    shouldAnimate || userClickedReveal
      ? CardExplosionAnimationWrapper
      : CardAnimationWrapper;

  return (
    <>
      {revelead &&
        (shouldAnimate || userClickedReveal) &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <Particles
              x={animationPosition.x}
              y={animationPosition.y}
              isHalloween={useHalloweenWrapping || wrappingType === 'halloween'}
              isValentines={
                useValentinesWrapping || wrappingType === 'valentines'
              }
            />
          </>,
          document.body,
        )}
      {noWidthContainer ? (
        <div style={{ width: '100%' }}>
          {!hideText && (
            <TitleContainer animated={shouldAnimate}>
              {revelead ? (
                <>
                  <StyledHeading3Text
                    key='revealed-title'
                    style={{ color: theme.text.title }}
                  >
                    {isClaimed
                      ? isReceiver
                        ? t('giftClaim.youveClaimedYourGift')
                        : t('giftClaim.thisGiftHasBeenClaimed')
                      : t('giftClaim.youveBeenGifted')}
                  </StyledHeading3Text>
                  {!isClaimed && !isCreator && (
                    <StyledHeading3Text style={{ color: theme.text.title }}>
                      <span style={{ fontWeight: 600 }}>
                        ${usdAmount} in {tokenSymbol}!
                      </span>
                    </StyledHeading3Text>
                  )}
                  <StyledParagraphLargeText style={{ fontWeight: 400 }}>
                    {isClaimed
                      ? isReceiver
                        ? t('giftClaim.descriptionClaimedReceiver')
                        : t('giftClaim.descriptionClaimedOther')
                      : t('giftClaim.descriptionNotClaimed')}
                  </StyledParagraphLargeText>
                </>
              ) : (
                <>
                  <StyledHeading3Text
                    key='secret-title'
                    style={{ color: theme.text.title }}
                  >
                    {wrappingType === 'breakpoint'
                      ? t('giftClaim.youveReceivedBreakpoint')
                      : t('giftClaim.youveReceivedGift')}
                  </StyledHeading3Text>
                  <StyledParagraphLargeText style={{ fontWeight: 400 }}>
                    {t('giftClaim.clickToReveal')}
                  </StyledParagraphLargeText>
                </>
              )}
            </TitleContainer>
          )}
          <ClaimGiftContainer>
            <GiftContainer>
              <GiftCardWrapper
                ref={setWrapperReference}
                animated={shouldAnimate || userClickedReveal}
                $fullWidth={noWidthContainer}
              >
                {revelead ? (
                  <CardAWrapper
                    key='revelead'
                    animated={shouldAnimate || userClickedReveal}
                    animationType='reveal'
                  >
                    <GiftCard
                      tokenAmount={tokenAmount}
                      usdAmount={usdAmount}
                      token={token}
                      cardImage={giftCardImageUrl}
                      lpd={lpd}
                      showEarnsText={showEarnsText}
                    />
                  </CardAWrapper>
                ) : (
                  <ClickableWrapper onClick={handleRevealClick}>
                    <CardAWrapper
                      key='hidden'
                      animated={shouldAnimate || userClickedReveal}
                      animationType={explotion ? 'explode' : 'reveal'}
                    >
                      <HiddenGiftCard
                        ref={setCardReference}
                        onLoad={handleImageLoad}
                        animationType={
                          explotion
                            ? 'explode'
                            : shouldAnimate || userClickedReveal
                              ? 'shake'
                              : 'occasional'
                        }
                        animationDuration={
                          explotion
                            ? '0.2s'
                            : shouldAnimate || userClickedReveal
                              ? '1.5s'
                              : '6s'
                        }
                        src={
                          WRAPPING_TYPE_MAP[
                            useHalloweenWrapping
                              ? 'halloween'
                              : useValentinesWrapping
                                ? 'valentines'
                                : wrappingType
                          ]
                        }
                      />
                    </CardAWrapper>
                    {showLabel && !userClickedReveal && !shouldAnimate && (
                      <ClickToRevealLabel>{revealText ?? t('giftClaim.clickToRevealLabel')}</ClickToRevealLabel>
                    )}
                  </ClickableWrapper>
                )}
              </GiftCardWrapper>
            </GiftContainer>
            <>
              {email && (
                <CardDetailRow $visible={userClickedReveal}>
                  <BodyLarge $variant='text-secondary'>{t('giftFlow.preview.giftFrom')}</BodyLarge>
                  <BodyLarge>{email}</BodyLarge>
                </CardDetailRow>
              )}
              {message && (
                <MessageContainer $visible={userClickedReveal}>
                  <BodyLarge $variant='text-secondary'>{t('giftFlow.delivery.message')}</BodyLarge>
                  <BodyLarge>{message}</BodyLarge>
                </MessageContainer>
              )}
              {!hideButton && userClickedReveal && (
                <>
                  {showAppDownload &&
                  isMobile &&
                  !Capacitor.isNativePlatform() ? (
                    <>
                      <StoreButton />
                      <BodyLarge $variant='text-secondary'>
                        {t('giftClaim.getGiftInApp')} <br />
                        {t('giftClaim.preferNotToInstall')}{' '}
                        <span
                          onClick={onClick}
                          style={{
                            textDecoration: 'underline',
                            color: theme.primary.primary,
                          }}
                        >
                          {t('giftClaim.continueInBrowser')}
                        </span>
                      </BodyLarge>
                    </>
                  ) : (
                    <StyledButton
                      onClick={onClick}
                      fullWidth={isMobile}
                      animated={shouldAnimate}
                      clickAnimation='bouncy'
                    >
                      {isClaimed
                        ? isReceiver
                          ? t('giftClaim.viewPortfolio')
                          : t('giftClaim.findAvailableGifts')
                        : (buttonText ?? t('giftClaim.claimGift'))}
                    </StyledButton>
                  )}
                </>
              )}
            </>
          </ClaimGiftContainer>
        </div>
      ) : (
        <WidthContainer>
          {!hideText && (
            <TitleContainer animated={shouldAnimate}>
              {revelead ? (
                <>
                  <StyledHeading3Text
                    key='revealed-title'
                    style={{ color: theme.text.title }}
                  >
                    {isClaimed
                      ? isReceiver
                        ? t('giftClaim.youveClaimedYourGift')
                        : t('giftClaim.thisGiftHasBeenClaimed')
                      : t('giftClaim.youveBeenGifted')}
                  </StyledHeading3Text>
                  {!isClaimed && !isCreator && (
                    <StyledHeading3Text style={{ color: theme.text.title }}>
                      <span style={{ fontWeight: 600 }}>
                        ${usdAmount} in {tokenSymbol}!
                      </span>
                    </StyledHeading3Text>
                  )}
                  <StyledParagraphLargeText style={{ fontWeight: 400 }}>
                    {isClaimed
                      ? isReceiver
                        ? t('giftClaim.descriptionClaimedReceiver')
                        : t('giftClaim.descriptionClaimedOther')
                      : t('giftClaim.descriptionNotClaimed')}
                  </StyledParagraphLargeText>
                </>
              ) : (
                <>
                  <StyledHeading3Text
                    key='secret-title'
                    style={{ color: theme.text.title }}
                  >
                    {wrappingType === 'breakpoint'
                      ? t('giftClaim.youveReceivedBreakpoint')
                      : t('giftClaim.youveReceivedGift')}
                  </StyledHeading3Text>
                  <StyledParagraphLargeText style={{ fontWeight: 400 }}>
                    {t('giftClaim.clickToReveal')}
                  </StyledParagraphLargeText>
                </>
              )}
            </TitleContainer>
          )}
          <ClaimGiftContainer>
            <GiftContainer>
              <GiftCardWrapper
                ref={setWrapperReference}
                animated={shouldAnimate || userClickedReveal}
              >
                {revelead ? (
                  <CardAWrapper
                    key='revelead'
                    animated={shouldAnimate || userClickedReveal}
                    animationType='reveal'
                  >
                    <GiftCard
                      tokenAmount={tokenAmount}
                      usdAmount={usdAmount}
                      token={token}
                      cardImage={giftCardImageUrl}
                      lpd={lpd}
                      showEarnsText={showEarnsText}
                    />
                  </CardAWrapper>
                ) : (
                  <ClickableWrapper onClick={handleRevealClick}>
                    <CardAWrapper
                      key='hidden'
                      animated={shouldAnimate || userClickedReveal}
                      animationType={explotion ? 'explode' : 'reveal'}
                    >
                      <HiddenGiftCard
                        ref={setCardReference}
                        onLoad={handleImageLoad}
                        animationType={
                          explotion
                            ? 'explode'
                            : shouldAnimate || userClickedReveal
                              ? 'shake'
                              : 'occasional'
                        }
                        animationDuration={
                          explotion
                            ? '0.2s'
                            : shouldAnimate || userClickedReveal
                              ? '1.5s'
                              : '6s'
                        }
                        src={
                          WRAPPING_TYPE_MAP[
                            useHalloweenWrapping ? 'halloween' : wrappingType
                          ]
                        }
                      />
                    </CardAWrapper>
                    {showLabel && !userClickedReveal && !shouldAnimate && (
                      <ClickToRevealLabel>{revealText ?? t('giftClaim.clickToRevealLabel')}</ClickToRevealLabel>
                    )}
                  </ClickableWrapper>
                )}
              </GiftCardWrapper>
            </GiftContainer>
            <>
              {email && (
                <CardDetailRow $visible={userClickedReveal}>
                  <BodyLarge $variant='text-secondary'>{t('giftFlow.preview.giftFrom')}</BodyLarge>
                  <BodyLarge>{email}</BodyLarge>
                </CardDetailRow>
              )}
              {message && (
                <MessageContainer $visible={userClickedReveal}>
                  <BodyLarge $variant='text-secondary'>{t('giftFlow.delivery.message')}</BodyLarge>
                  <BodyLarge>{message}</BodyLarge>
                </MessageContainer>
              )}
              {!hideButton && userClickedReveal && (
                <>
                  {showAppDownload &&
                  isMobile &&
                  !Capacitor.isNativePlatform() ? (
                    <>
                      <StoreButton />
                      <BodyLarge $variant='text-secondary'>
                        {t('giftClaim.getGiftInApp')} <br />
                        {t('giftClaim.preferNotToInstall')}{' '}
                        <span
                          onClick={onClick}
                          style={{
                            textDecoration: 'underline',
                            color: theme.primary.primary,
                          }}
                        >
                          {t('giftClaim.continueInBrowser')}
                        </span>
                      </BodyLarge>
                    </>
                  ) : (
                    <StyledButton
                      onClick={onClick}
                      fullWidth={isMobile}
                      animated={shouldAnimate}
                      clickAnimation='bouncy'
                    >
                      {isClaimed
                        ? isReceiver
                          ? t('giftClaim.viewPortfolio')
                          : t('giftClaim.findAvailableGifts')
                        : (buttonText ?? t('giftClaim.claimGift'))}
                    </StyledButton>
                  )}
                </>
              )}
            </>
          </ClaimGiftContainer>
        </WidthContainer>
      )}
    </>
  );
};

export default PresentationalGift;
